import { jsPDF } from 'jspdf'

const PAGE_WIDTH = 612
const PAGE_HEIGHT = 792
const SIDEBAR_WIDTH = 200
const SIDEBAR_X = 20
const SIDEBAR_CONTENT_WIDTH = SIDEBAR_WIDTH - SIDEBAR_X * 2
const MAIN_X = SIDEBAR_WIDTH + 32
const MAIN_WIDTH = PAGE_WIDTH - MAIN_X - 40
const HEADER_HEIGHT = 118
const MAIN_START_Y = HEADER_HEIGHT + 20

const ACCENT = [79, 70, 229]
const ACCENT_LIGHT = [238, 242, 255]
const TEXT_DARK = [15, 23, 42]
const TEXT_BODY = [51, 65, 85]
const TEXT_MUTED = [100, 116, 139]

const HEADER_BG = '#eef2ff'

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = reject
    const url = src.startsWith('/') ? `${window.location.origin}${src}` : src
    img.src = url
  })
}

function lineHeight(fontSize, leading = 1.4) {
  return fontSize * leading
}

function drawSidebarBackground(doc) {
  doc.setFillColor(...ACCENT)
  doc.rect(0, 0, SIDEBAR_WIDTH, PAGE_HEIGHT, 'F')
}

function drawMainHeader(doc, profile, info) {
  doc.setFillColor(...ACCENT_LIGHT)
  doc.rect(SIDEBAR_WIDTH, 0, PAGE_WIDTH - SIDEBAR_WIDTH, HEADER_HEIGHT, 'F')

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(21)
  doc.setTextColor(...TEXT_DARK)
  doc.text(info.name, MAIN_X, 40)

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(11)
  doc.setTextColor(...ACCENT)
  doc.text(profile.title, MAIN_X, 58)

  doc.setFontSize(9)
  doc.setTextColor(...TEXT_MUTED)
  doc.text('3+ Years Professional Experience  ·  Innsite IT Solution', MAIN_X, 74)
}

function clipImageToCircle(img, outputSize, backgroundColor = HEADER_BG) {
  const sourceWidth = img.naturalWidth || img.width
  const sourceHeight = img.naturalHeight || img.height
  const scale = Math.max(outputSize / sourceWidth, outputSize / sourceHeight)
  const width = sourceWidth * scale
  const height = sourceHeight * scale
  const offsetX = (outputSize - width) / 2
  const offsetY = (outputSize - height) / 2

  const masked = document.createElement('canvas')
  masked.width = outputSize
  masked.height = outputSize
  const maskedCtx = masked.getContext('2d')
  maskedCtx.drawImage(img, offsetX, offsetY, width, height)
  maskedCtx.globalCompositeOperation = 'destination-in'
  maskedCtx.beginPath()
  maskedCtx.arc(outputSize / 2, outputSize / 2, outputSize / 2, 0, Math.PI * 2)
  maskedCtx.fill()

  const canvas = document.createElement('canvas')
  canvas.width = outputSize
  canvas.height = outputSize
  const ctx = canvas.getContext('2d')
  ctx.fillStyle = backgroundColor
  ctx.fillRect(0, 0, outputSize, outputSize)
  ctx.drawImage(masked, 0, 0)

  return canvas.toDataURL('image/jpeg', 0.92)
}

function drawProfilePhoto(doc, image) {
  const size = 76
  const x = PAGE_WIDTH - size - 36
  const y = 26
  const cx = x + size / 2
  const cy = y + size / 2
  const radius = size / 2
  const circularImage = clipImageToCircle(image, size * 4)

  doc.addImage(circularImage, 'JPEG', x, y, size, size)
  doc.setDrawColor(...ACCENT)
  doc.setLineWidth(2.5)
  doc.circle(cx, cy, radius + 2, 'S')
}

function ensureSpace(doc, y, needed) {
  if (y + needed > PAGE_HEIGHT - 48) {
    doc.addPage()
    drawSidebarBackground(doc)
    return MAIN_START_Y
  }
  return y
}

function sidebarSectionTitle(doc, title, y) {
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(9)
  doc.setTextColor(255, 255, 255)
  doc.text(title.toUpperCase(), SIDEBAR_X, y)
  doc.setDrawColor(255, 255, 255)
  doc.setLineWidth(0.5)
  doc.line(SIDEBAR_X, y + 4, SIDEBAR_X + 44, y + 4)
  return y + 18
}

function sidebarWrappedText(doc, text, y, { bold = false, fontSize = 8.5, color = [230, 230, 250] } = {}) {
  doc.setFont('helvetica', bold ? 'bold' : 'normal')
  doc.setFontSize(fontSize)
  doc.setTextColor(...color)
  const lines = doc.splitTextToSize(text, SIDEBAR_CONTENT_WIDTH)
  doc.text(lines, SIDEBAR_X, y)
  return y + lines.length * lineHeight(fontSize) + 4
}

function sidebarSkillGroup(doc, label, skills, y) {
  y = sidebarWrappedText(doc, label.toUpperCase(), y, {
    bold: true,
    fontSize: 7.5,
    color: [199, 210, 254],
  })
  return sidebarWrappedText(doc, skills.join(', '), y, { fontSize: 8, color: [241, 245, 249] })
}

function mainSectionTitle(doc, title, y) {
  const label = title.toUpperCase()
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.setTextColor(...TEXT_DARK)
  doc.text(label, MAIN_X, y)

  const underlineWidth = doc.getTextWidth(label)
  doc.setFillColor(...ACCENT)
  doc.rect(MAIN_X, y + 5, underlineWidth, 2, 'F')

  return y + 24
}

function mainWrapped(doc, text, x, y, width, fontSize = 9.5) {
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(fontSize)
  doc.setTextColor(...TEXT_BODY)
  const lines = doc.splitTextToSize(text, width)
  doc.text(lines, x, y)
  return y + lines.length * lineHeight(fontSize) + 2
}

function mainBullets(doc, items, x, y, width) {
  let cursorY = y
  const bulletX = x
  const textX = x + 12
  const textWidth = width - 12

  for (const item of items) {
    cursorY = ensureSpace(doc, cursorY, 20)
    doc.setFillColor(...ACCENT)
    doc.circle(bulletX + 2, cursorY - 3, 2, 'F')
    cursorY = mainWrapped(doc, item, textX, cursorY, textWidth, 9)
    cursorY += 6
  }
  return cursorY
}

function drawJobHeader(doc, job, y) {
  const periodLabel = job.duration ? `${job.period} (${job.duration})` : job.period
  const dateColumnWidth = 108
  const roleWidth = MAIN_WIDTH - dateColumnWidth

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(10.5)
  doc.setTextColor(...TEXT_DARK)
  const roleLines = doc.splitTextToSize(job.role, roleWidth)
  doc.text(roleLines, MAIN_X, y)

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8.5)
  doc.setTextColor(...ACCENT)
  doc.text(periodLabel, MAIN_X + MAIN_WIDTH, y, { align: 'right' })

  y += roleLines.length * lineHeight(10.5) + 4

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(9)
  doc.setTextColor(...TEXT_MUTED)
  doc.text(`${job.company} · ${job.location}`, MAIN_X, y)

  return y + 16
}

export async function generateAndSaveResumePdf({
  profile,
  resumeEducation,
  experience,
  pdfSkills,
  personalSkills,
  images,
  fileName = 'Resume - Angelo Mayuga.pdf',
}) {
  const doc = new jsPDF('p', 'pt', 'letter')
  const info = profile.informations

  drawSidebarBackground(doc)
  drawMainHeader(doc, profile, info)

  try {
    const idPicture = await loadImage(images.resumeId)
    drawProfilePhoto(doc, idPicture)
  } catch {
    // optional photo
  }

  // Sidebar
  let sideY = 36

  sideY = sidebarSectionTitle(doc, 'Contact', sideY)
  sideY = sidebarWrappedText(doc, info.email, sideY)
  sideY = sidebarWrappedText(doc, info.contact, sideY)
  sideY = sidebarWrappedText(doc, info.homeaddress, sideY)
  sideY = sidebarWrappedText(doc, info.Website.replace('https://', ''), sideY)
  if (profile.social?.linkedin) {
    sideY = sidebarWrappedText(doc, 'linkedin.com/in/mayuga-angelo-p-633251269', sideY)
  }
  sideY += 10

  sideY = sidebarSectionTitle(doc, 'Education', sideY)
  sideY = sidebarWrappedText(doc, resumeEducation.degree, sideY, {
    bold: true,
    fontSize: 8.5,
    color: [255, 255, 255],
  })
  sideY = sidebarWrappedText(doc, resumeEducation.school, sideY)
  sideY = sidebarWrappedText(doc, `${resumeEducation.period} · ${resumeEducation.location}`, sideY)
  sideY += 10

  sideY = sidebarSectionTitle(doc, 'Technical Skills', sideY)
  sideY = sidebarSkillGroup(doc, 'Frontend', pdfSkills.frontend, sideY)
  sideY = sidebarSkillGroup(doc, 'Backend', pdfSkills.backend, sideY)
  sideY = sidebarSkillGroup(doc, 'Database', pdfSkills.database, sideY)
  sideY = sidebarSkillGroup(doc, 'AI & Agents', pdfSkills.ai, sideY)
  if (pdfSkills.devops?.length) {
    sideY = sidebarSkillGroup(doc, 'DevOps', pdfSkills.devops, sideY)
  }
  sideY = sidebarSkillGroup(doc, 'Tools', pdfSkills.tools, sideY)
  sideY += 6

  sideY = sidebarSectionTitle(doc, 'Core Strengths', sideY)
  sidebarWrappedText(doc, personalSkills.join(' · '), sideY)

  // Main content
  let y = MAIN_START_Y

  y = mainSectionTitle(doc, 'Professional Summary', y)
  y = mainWrapped(doc, info.summary, MAIN_X, y, MAIN_WIDTH)
  y += 16

  y = ensureSpace(doc, y, 64)
  y = mainSectionTitle(doc, 'Work Experience', y)

  for (const job of experience) {
    y = ensureSpace(doc, y, 72)
    y = drawJobHeader(doc, job, y)
    y = mainBullets(doc, job.highlights, MAIN_X, y, MAIN_WIDTH)
    y += 12
  }

  doc.save(fileName)
}
